<template>
  <div class="company-flow">
    <profile-company-flow-step
      v-for="step in resolvedSteps"
      :key="step.key"
      :step="step"
      @open-document="openDocument"
      @archive-document="$emit('archive-sequence', $event)"
    />
  </div>
</template>

<script>
import moment from 'moment-timezone'
import { createNamespacedHelpers } from 'vuex'
import ProfileCompanyFlowStep from './profile-company-flow-step.vue'

const { mapActions: modalActions } = createNamespacedHelpers('modals')
const { mapState: profileState } = createNamespacedHelpers('profile')

const FINAL_STATUSES = ['approved', 'completed', 'complete', 'signed', 'accepted', 'actual']
const DANGER_STATUSES = ['rejected', 'declined', 'expired', 'missing']
const STATUS_COLORS = {
  completed: '#10b981',
  inProgress: '#f59e0b',
  danger: '#ef4444',
  muted: '#6b7280'
}

export default {
  name: 'ProfileCompanyFlow',
  components: {
    'profile-company-flow-step': ProfileCompanyFlowStep
  },
  props: {
    flow: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      default: 'documents'
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    resolvedSteps() {
      const firstOpenStep = this.flowSteps.find(step => !step.completed && !step.muted)

      return this.flowSteps.map(step => ({
        ...step,
        current: firstOpenStep?.key === step.key
      }))
    },

    normalizedPaymentType() {
      return String(this.associate?.payment_info?.type ?? '').toLowerCase()
    },

    flowSteps() {
      if (this.mode === 'trainings') {
        return [this.resolveStep(this.flow.trainings, 'trainings', 'Trainings', { emptyMuted: true })]
      }

      const middleStepKey = this.normalizedPaymentType === 'contractor' ? 'orientation' : 'onboarding'
      const middleStepTitle = this.normalizedPaymentType === 'contractor' ? 'Orientation' : 'Onboarding'
      const departureHasData = Boolean(this.flow.departure?.documents?.length || this.flow.departure?.title)
      const isSeparated = Boolean(this.associate?.termination || !this.associate?.is_active || departureHasData)

      return [
        this.resolveStep(this.flow.invite, 'invite', 'Invite', { emptyMuted: true }),
        this.resolveStep(this.flow[middleStepKey], middleStepKey, middleStepTitle, { emptyMuted: true }),
        this.resolveStep(this.flow.departure, 'departure', 'Separation', {
          danger: isSeparated,
          emptyMuted: !isSeparated,
          description: isSeparated ? this.flow.departure?.title : 'Not applicable - associate is active'
        })
      ]
    }
  },
  methods: {
    ...modalActions(['openModal']),

    isDocumentCompleted(document, stepKey) {
      if (stepKey === 'invite') {
        return Boolean(document.signed_date || document.complete_date || document.completed_date)
      }

      const normalizedStatus = String(document.status ?? '').toLowerCase()

      return FINAL_STATUSES.includes(normalizedStatus)
    },

    getDocumentStatusColor(status) {
      const normalizedStatus = String(status ?? '').toLowerCase()

      if (FINAL_STATUSES.includes(normalizedStatus)) return STATUS_COLORS.completed
      if (DANGER_STATUSES.includes(normalizedStatus)) return STATUS_COLORS.danger
      if (normalizedStatus) return STATUS_COLORS.inProgress

      return STATUS_COLORS.muted
    },

    formatDate(date) {
      if (!date) return undefined

      const parsed = moment(date)

      return parsed.isValid() ? parsed.format('MMM DD YYYY') : date
    },

    getDocumentDate(document) {
      return document.signed_date || document.complete_date || document.completed_date
    },

    normalizeDocument(document, stepKey, index) {
      const isSequence = Boolean(document.signed_sequence_id)
      const date = this.getDocumentDate(document)
      const completed = this.isDocumentCompleted(document, stepKey)
      const status = document.status || (completed ? 'Completed' : undefined)

      return {
        ...document,
        key: `${stepKey}-${document.document_id || document.sequence_id || index}`,
        flowStepKey: stepKey,
        flowType: isSequence ? 'sequence' : 'document',
        name: document.name || 'Untitled',
        completed,
        status,
        statusColor: this.getDocumentStatusColor(status),
        meta: [date ? this.formatDate(date) : null, document.sequence_id ? `Sequence #${document.sequence_id}` : null]
          .filter(Boolean)
          .join(' · '),
        canOpen: Boolean(isSequence ? document.signed_sequence_id : document.document_id && document.version),
        canArchive: isSequence
      }
    },

    resolveStep(rawStep, key, title, options = {}) {
      const documents = (rawStep?.documents ?? []).map((document, index) =>
        this.normalizeDocument(document, key, index)
      )
      const hasDocuments = documents.length > 0
      const completed = hasDocuments && documents.every(document => this.isDocumentCompleted(document, key))
      const date = documents.map(this.getDocumentDate).filter(Boolean).slice(-1)[0]
      const muted = options.muted || (!hasDocuments && options.emptyMuted)
      const danger = options.danger

      return {
        key,
        title,
        documents,
        completed,
        muted,
        danger,
        date: this.formatDate(date),
        description: options.description || rawStep?.title,
        statusTitle: muted ? 'Not applicable' : completed ? 'Completed' : 'In progress',
        statusColor: muted
          ? STATUS_COLORS.muted
          : danger
            ? STATUS_COLORS.danger
            : completed
              ? STATUS_COLORS.completed
              : STATUS_COLORS.inProgress
      }
    },

    openDocument(document) {
      if (document.flowType === 'sequence') {
        this.openModal({
          type: 'sequenceReview',
          data: {
            visible: true,
            associate: this.associate,
            sequence_id: document.signed_sequence_id,
            onSave: () => {}
          }
        })

        return
      }

      this.openModal({
        type: 'documentView',
        data: {
          visible: true,
          associate_id: this.associate.id,
          version: document.version,
          document_id: document.document_id
        }
      })
    }
  }
}
</script>

<style scoped>
.company-flow {
  display: flex;
  flex-direction: column;
}
</style>
